import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Search, X, ChevronUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';
import ServiceCallModal from '../components/service_calls/ServiceCallModal';

const STATUS_COLORS = { open: '#10B981', closed: '#6B7280' };
const STATUS_LABELS = { open: 'פתוחה', closed: 'סגורה' };

export default function ServiceCalls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [typeId, setTypeId] = useState('');
  const [page, setPage] = useState(1);
  const [sortDir, setSortDir] = useState('desc');
  const [pages, setPages] = useState(1);

  const [editingCall, setEditingCall] = useState(null);

  async function deleteCall(sc) {
    if (!window.confirm(`למחוק קריאת שירות של ${sc.customer_name}?`)) return;
    try {
      await api.delete(`/service-calls/${sc.id}`);
      loadCalls();
    } catch {
      alert('שגיאה במחיקת קריאת שירות');
    }
  }

  useEffect(() => {
    api.get('/service-call-types').then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_dir: sortDir };
      if (search) params.search = search;
      if (status) params.status = status;
      if (typeId) params.type_id = typeId;

      const res = await api.get('/service-calls', { params });
      setCalls(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, typeId, sortDir]);

  useEffect(() => { loadCalls(); }, [loadCalls]);
  useEffect(() => { setPage(1); }, [search, status, typeId]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Wrench className="text-primary-600 dark:text-primary-400" size={22} />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">קריאות שירות</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">({total.toLocaleString()})</span>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי שם לקוח..."
              className="w-full pr-9 pl-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                       bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסטטוסים</option>
            <option value="open">פתוחות</option>
            <option value="closed">סגורות</option>
          </select>

          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                       bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסוגים</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          {(search || status !== 'open' || typeId) && (
            <button
              onClick={() => { setSearch(''); setStatus('open'); setTypeId(''); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={14} /> נקה
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">לקוח</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">עיר</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">סוג</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">סטטוס</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">פירוט</th>
                <th
                  className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                >
                  <span className="flex items-center gap-1 justify-center">
                    תאריך
                    {sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </span>
                </th>
                <th className="px-3 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {loading && calls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                    </div>
                  </td>
                </tr>
              ) : calls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">אין קריאות שירות</td>
                </tr>
              ) : (
                calls.map((sc) => (
                  <tr
                    key={sc.id}
                    onClick={() => setEditingCall(sc)}
                    className={`border-b border-gray-100 dark:border-gray-700/50 hover:brightness-95 dark:hover:brightness-110 cursor-pointer ${
                      sc.status === 'open'
                        ? 'bg-emerald-50/40 dark:bg-emerald-900/10'
                        : 'bg-gray-50/40 dark:bg-gray-700/20'
                    }`}
                  >
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers/${sc.customer_id}`); }}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {sc.customer_name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                      {sc.customer_city || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {sc.type_name ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">
                          {sc.type_name}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={sc.status}
                        onChange={async (e) => {
                          try {
                            await api.put(`/service-calls/${sc.id}`, { status: e.target.value });
                            loadCalls();
                          } catch { alert('שגיאה בעדכון סטטוס'); }
                        }}
                        className="text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700"
                        style={{ borderColor: STATUS_COLORS[sc.status], color: STATUS_COLORS[sc.status] }}
                      >
                        <option value="open">פתוחה</option>
                        <option value="closed">סגורה</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 max-w-xs truncate">
                      {sc.details || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500 text-xs">
                      {format(parseISO(sc.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-0.5 justify-center">
                        <button
                          onClick={() => setEditingCall(sc)}
                          title="עריכה"
                          className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => deleteCall(sc)}
                          title="מחיקה"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">עמוד {page} מתוך {pages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                הקודם
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>

      {editingCall && (
        <ServiceCallModal
          call={editingCall}
          onClose={() => setEditingCall(null)}
          onSaved={() => { setEditingCall(null); loadCalls(); }}
        />
      )}
    </div>
  );
}
